import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassContainer } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';

const Chat = ({ apiBaseUrl, token, userInfo }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/chat/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data.messages || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  // Initial load
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      fetchMessages();
    }, 10000);
    return () => clearInterval(interval);
  }, [token, fetchMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const response = await axios.post(
        `${apiBaseUrl}/api/chat/messages`,
        { text: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNewMessage('');
        // Add the new message immediately for better UX
        setMessages((prev) => [...prev, response.data.message]);
        // Then fetch all messages to sync
        setTimeout(fetchMessages, 500);
      } else {
        setError(response.data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
      // Focus back on input
      inputRef.current?.focus();
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await axios.delete(
        `${apiBaseUrl}/api/chat/messages/${messageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      } else {
        setError(response.data.message || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return '';
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const isAdmin = userInfo?.is_admin;
  const currentUsername = userInfo?.username;

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Show notification for new messages from other users
  const previousMessagesRef = useRef([]);
  useEffect(() => {
    if (messages.length > 0 && previousMessagesRef.current.length > 0) {
      const newMessages = messages.filter(
        msg => !previousMessagesRef.current.some(prev => prev.id === msg.id)
      );
      
      newMessages.forEach(message => {
        // Only notify for messages from other users
        if (message.username !== currentUsername && document.hidden) {
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(`New message from ${message.username}`, {
              body: message.text,
              icon: '/icon-192.png',
              badge: '/icon-192.png',
              tag: `chat-${message.id}`,
              requireInteraction: false,
            });
          }
        }
      });
    }
    previousMessagesRef.current = messages;
  }, [messages, currentUsername]);

  return (
    <GlassContainer
      sx={{
        maxWidth: { xs: '100%', sm: '800px', md: '900px' },
        mx: 'auto',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 2, sm: 3 },
          pb: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ChatIcon sx={{ color: '#00d4ff', fontSize: { xs: 24, sm: 28 } }} />
          <Typography
            variant="h5"
            sx={{
              color: (theme) => theme.palette.primary.main,
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Chat
          </Typography>
        </Box>
        <Tooltip title="Refresh messages">
          <IconButton
            onClick={fetchMessages}
            disabled={loading}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: '#00d4ff' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <Paper
        ref={messagesContainerRef}
        elevation={0}
        sx={{
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          height: { xs: '350px', sm: '400px', md: '450px' },
          maxHeight: '60vh',
          overflowY: 'auto',
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          },
        }}
      >
        {loading && messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress sx={{ color: '#00d4ff' }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <ChatIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {t('chat.noMessages')}
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <Box key={date}>
              {/* Date Divider */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  my: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    color: 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                >
                  {date}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
              </Box>

              {/* Messages for this date */}
              {dateMessages.map((message, index) => {
                const isOwnMessage = message.username === currentUsername;
                const canDelete = isAdmin || isOwnMessage;

                return (
                  <Fade in key={message.id} timeout={300}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        gap: { xs: 1, sm: 1.5 },
                        mb: { xs: 1, sm: 1.5 },
                      }}
                    >
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: { xs: 32, sm: 36 },
                          height: { xs: 32, sm: 36 },
                          borderRadius: '50%',
                          background: isOwnMessage
                            ? 'linear-gradient(135deg, #00d4ff, #0099ff)'
                            : 'linear-gradient(135deg, #ff6b6b, #ee5a5a)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          color: '#fff',
                          flexShrink: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {message.username.toLowerCase() === 'piotrek' ? (
                          <img
                            src="/hacker.png"
                            alt="Piotrek"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          message.username.charAt(0).toUpperCase()
                        )}
                      </Box>

                      {/* Message Bubble */}
                      <Box
                        sx={{
                          maxWidth: { xs: '75%', sm: '70%' },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {/* Username and Time */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 1 },
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              color: isOwnMessage ? '#00d4ff' : '#ff6b6b',
                            }}
                          >
                            {message.username}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              color: 'rgba(255, 255, 255, 0.4)'
                            }}
                          >
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                          {canDelete && (
                            <Tooltip title="Delete message">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteMessage(message.id)}
                                sx={{
                                  p: 0.25,
                                  color: 'rgba(255, 255, 255, 0.3)',
                                  '&:hover': {
                                    color: '#ff4444',
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        {/* Message Text */}
                        <Paper
                          elevation={0}
                          sx={{
                            background: isOwnMessage
                              ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(0, 153, 255, 0.1))'
                              : 'rgba(255, 255, 255, 0.08)',
                            borderRadius: isOwnMessage
                              ? '16px 16px 4px 16px'
                              : '16px 16px 16px 4px',
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 0.75, sm: 1 },
                            border: `1px solid ${
                              isOwnMessage
                                ? 'rgba(0, 212, 255, 0.3)'
                                : 'rgba(255, 255, 255, 0.1)'
                            }`,
                            wordBreak: 'break-word',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                              color: 'rgba(255, 255, 255, 0.9)',
                              lineHeight: 1.4,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {message.text}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          ))
        )}
      </Paper>

      {/* Input Area */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          alignItems: 'flex-end',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          placeholder={t('chat.typeMessage')}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={sending}
          multiline
          maxRows={3}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00d4ff',
              },
            },
            '& .MuiInputBase-input': {
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.4)',
              },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          disabled={!newMessage.trim() || sending}
          sx={{
            minWidth: { xs: '44px', sm: '56px' },
            height: { xs: '44px', sm: '56px' },
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #00d4ff, #0099ff)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00e4ff, #00a9ff)',
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {sending ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            <SendIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          )}
        </Button>
      </Box>

      {/* Character Count */}
      <Typography
        variant="caption"
        sx={{
          textAlign: 'right',
          display: 'block',
          mt: 0.5,
          fontSize: { xs: '0.65rem', sm: '0.75rem' },
          color:
            newMessage.length > 900
              ? '#ff6b6b'
              : 'rgba(255, 255, 255, 0.4)',
        }}
      >
        {newMessage.length}/1000
      </Typography>
    </GlassContainer>
  );
};

export default Chat;
